import React from 'react';
import { Card } from '../ui';
import { account } from '../copData';
import { useCop } from '../CopApp';

// Color + text fields share the campaign builder's Styling controls so the
// Brand Guide reads as the same surface operators already know.
function BrandField({ keyName, label, type, hint, span, options, placeholder, brand, onChange }) {
	return (
		<label className={`cop-form-field${span ? ' cop-form-span' : ''}`}>
			<span>{label}{hint && <em className="cop-field-hint">{hint}</em>}</span>
			{type === 'color' ? (
				<div className="cop-color-field">
					<input type="color" value={brand[keyName]} onChange={onChange(keyName)} aria-label={label} />
					<input className="cop-input" value={brand[keyName]} onChange={onChange(keyName)} spellCheck={false} />
				</div>
			) : type === 'textarea' ? (
				<textarea className="cop-input" rows={4} value={brand[keyName]} onChange={onChange(keyName)} placeholder={placeholder} />
			) : type === 'select' ? (
				<select className="cop-select" value={brand[keyName]} onChange={onChange(keyName)}>
					{options.map((o) => (
						<option key={o.value} value={o.value}>{o.label}</option>
					))}
				</select>
			) : (
				<input className="cop-input" value={brand[keyName]} onChange={onChange(keyName)} placeholder={placeholder} spellCheck={false} />
			)}
		</label>
	);
}

const SWATCHES = [
	['primaryColor', 'Primary'],
	['secondaryColor', 'Secondary'],
	['accentColor', 'Accent'],
	['backgroundColor', 'Background'],
	['textColor', 'Text'],
	['mutedColor', 'Muted'],
	['linkColor', 'Link'],
];

// Live brand preview — a campaign panel rendered with the brand system, mirroring
// the builder's DesignPreview look (gradient panel, headline, accent button).
function BrandPreview({ brand }) {
	const panelStyle = {
		borderRadius: 16,
		padding: 30,
		background: `linear-gradient(135deg, ${brand.primaryColor}, ${brand.secondaryColor})`,
		color: brand.textColor,
		fontFamily: brand.fontFamily,
		fontSize: brand.baseFontSize,
		maxWidth: 420,
	};
	const outline = brand.buttonStyle === 'outline';
	const btnStyle = {
		background: outline ? 'transparent' : brand.accentColor,
		border: outline ? `1px solid ${brand.accentColor}` : '0',
		color: outline ? brand.accentColor : brand.buttonTextColor,
		borderRadius: brand.buttonRadius,
		padding: brand.buttonPadding,
		fontSize: '0.9em',
	};
	return (
		<div className="cop-dz-modal" style={panelStyle}>
			<div className="cop-dz-eyeline" style={{ fontSize: '0.75em', color: brand.textColor, opacity: 0.85 }}>
				{brand.logoUrl ? (
					<img src={brand.logoUrl} alt={account.merchant_name} style={{ height: 22, display: 'block' }} />
				) : (
					account.merchant_name
				)}
			</div>
			<div
				className="cop-dz-headline"
				style={{ fontSize: brand.headlineSize, color: brand.textColor, fontFamily: brand.headingFont, fontWeight: brand.headingWeight, letterSpacing: `${brand.letterSpacing}px` }}
			>
				{brand.tagline || 'Your headline here'}
			</div>
			<div className="cop-dz-sub" style={{ color: brand.mutedColor }}>
				Complete your order now and we’ll take care of the rest.
			</div>
			<button className="cop-dz-btn" style={btnStyle} onClick={(e) => e.preventDefault()}>
				Shop Now
			</button>
		</div>
	);
}

export default function BrandGuide() {
	const { brandGuide, setBrandGuide, showToast } = useCop();
	const set = (key) => (e) => setBrandGuide((b) => ({ ...b, [key]: e.target.value }));

	return (
		<div className="cop-design-split">
			<div className="cop-design-main">
				<Card title="Brand Identity" icon="✦" right={account.merchant_name}>
					<div className="cop-form-grid">
						<BrandField keyName="logoUrl" label="Logo (dark bg)" hint="URL" type="text" placeholder="https://…/logo.svg" brand={brandGuide} onChange={set} />
						<BrandField keyName="logoLightUrl" label="Logo (light bg)" hint="URL" type="text" placeholder="https://…/logo-dark.svg" brand={brandGuide} onChange={set} />
						<BrandField keyName="faviconUrl" label="Favicon / icon" hint="URL" type="text" placeholder="https://…/favicon.png" brand={brandGuide} onChange={set} />
						<BrandField keyName="brandGuidelinesUrl" label="Brand guidelines" hint="Figma / doc link" type="text" placeholder="https://figma.com/…" brand={brandGuide} onChange={set} />
						<BrandField keyName="tagline" label="Tagline" type="text" span brand={brandGuide} onChange={set} />
					</div>
				</Card>

				<Card title="Brand Colors" icon="◐">
					<div className="cop-style-group">
						<div className="cop-form-grid">
							<BrandField keyName="primaryColor" label="Primary" type="color" brand={brandGuide} onChange={set} />
							<BrandField keyName="secondaryColor" label="Secondary" type="color" brand={brandGuide} onChange={set} />
							<BrandField keyName="accentColor" label="Accent / CTA" type="color" brand={brandGuide} onChange={set} />
							<BrandField keyName="backgroundColor" label="Background" type="color" brand={brandGuide} onChange={set} />
							<BrandField keyName="textColor" label="Text" type="color" brand={brandGuide} onChange={set} />
							<BrandField keyName="mutedColor" label="Muted text" type="color" brand={brandGuide} onChange={set} />
							<BrandField keyName="linkColor" label="Link" type="color" brand={brandGuide} onChange={set} />
							<BrandField keyName="buttonTextColor" label="Button text" type="color" brand={brandGuide} onChange={set} />
						</div>
					</div>
				</Card>

				<Card title="Typography" icon="A">
					<div className="cop-form-grid">
						<BrandField keyName="headingFont" label="Heading font" type="text" brand={brandGuide} onChange={set} />
						<BrandField keyName="fontFamily" label="Body font" type="text" brand={brandGuide} onChange={set} />
						<BrandField keyName="baseFontSize" label="Base font size" type="text" brand={brandGuide} onChange={set} />
						<BrandField
							keyName="headingWeight"
							label="Heading weight"
							type="select"
							options={[
								{ value: '400', label: 'Regular (400)' },
								{ value: '500', label: 'Medium (500)' },
								{ value: '600', label: 'Semibold (600)' },
								{ value: '700', label: 'Bold (700)' },
								{ value: '800', label: 'Extrabold (800)' },
							]}
							brand={brandGuide}
							onChange={set}
						/>
						<BrandField keyName="headlineSize" label="Headline size" type="text" brand={brandGuide} onChange={set} />
						<BrandField keyName="letterSpacing" label="Letter spacing (px)" type="text" brand={brandGuide} onChange={set} />
					</div>
				</Card>

				<Card title="Buttons" icon="⬚">
					<div className="cop-form-grid">
						<BrandField
							keyName="buttonStyle"
							label="Style"
							type="select"
							options={[
								{ value: 'solid', label: 'Solid' },
								{ value: 'outline', label: 'Outline' },
							]}
							brand={brandGuide}
							onChange={set}
						/>
						<BrandField keyName="buttonRadius" label="Radius" type="text" brand={brandGuide} onChange={set} />
						<BrandField keyName="buttonPadding" label="Padding" type="text" span brand={brandGuide} onChange={set} />
					</div>
				</Card>

				<Card title="Voice & Tone" icon="✍">
					<div className="cop-form-grid">
						<BrandField keyName="voice" label="Brand voice" hint="guides AI-generated copy" type="textarea" span brand={brandGuide} onChange={set} />
						<BrandField keyName="preferredWords" label="Words to use" hint="comma-separated" type="text" span brand={brandGuide} onChange={set} />
						<BrandField keyName="avoidWords" label="Words to avoid" hint="comma-separated" type="text" span brand={brandGuide} onChange={set} />
						<BrandField
							keyName="emojiUsage"
							label="Emoji usage"
							type="select"
							options={[
								{ value: 'none', label: 'Never' },
								{ value: 'sparingly', label: 'Sparingly' },
								{ value: 'freely', label: 'Freely' },
							]}
							brand={brandGuide}
							onChange={set}
						/>
					</div>
				</Card>
			</div>

			<div className="cop-design-side">
				<Card title="Brand Preview" icon="▣" right={<button className="cop-btn small" onClick={() => showToast('Brand guide saved')}>Save</button>}>
					<div className="cop-preview-stage">
						<BrandPreview brand={brandGuide} />
					</div>
					<div className="cop-brand-swatches">
						{SWATCHES.map(([key, label]) => (
							<div className="cop-brand-swatch" key={key}>
								<span className="sw" style={{ background: brandGuide[key] }} />
								<span className="lb">{label}</span>
								<span className="hx cop-mono">{brandGuide[key]}</span>
							</div>
						))}
					</div>
					<div className="cop-design-preview-meta">
						Campaigns inherit these defaults — operators can still override per campaign in the Builder’s Design tab.
					</div>
				</Card>
			</div>
		</div>
	);
}
